import { Accessor, Component, createSignal, createMemo, Show } from 'solid-js';
import {
  TagEditor,
  Separators,
  ITagItem,
  ConfirmPopup,
  BtnClasses,
  PanelLayout,
  Button,
} from '@tcweb/connect-ui';
import { TcTcpsApiService, TcRegion } from '@data-access/connect/tc-tcps';
import { UvIconButton } from '../../../button/UvIconButton';
import { t } from '@trimble/i18n';
import { useLogger } from '@trimble/solidjs-utils';
import { NotificationEventBus } from '@trimble/solidjs-notifications';
import { asError } from '@trimble/logging';
import type { ReservedViewDataConfig } from './reserved-view-data.types';

export interface CreateViewFormData {
  name: string;
  description: string;
  tags: ITagItem[];
  sharedWith: ITagItem[];
}

export interface CreateViewTabProps {
  onClose(): void;
  /** Must return the created view ID */
  onSave(formData: CreateViewFormData): Promise<string>;
  hasModelsLoaded?: Accessor<boolean>;
  tcTcpsApi: TcTcpsApiService;
  region: Accessor<TcRegion | undefined>;
  projectId: Accessor<string | undefined>;
  reservedViewDataConfig?: ReservedViewDataConfig;
  onViewCreated?(viewId: string): void;
}

export const CreateViewTab: Component<CreateViewTabProps> = (props) => {
  const logger = useLogger().getLogger('CreateViewTab');

  const [name, setName] = createSignal('');
  const [description, setDescription] = createSignal('');
  const [tags, setTags] = createSignal<ITagItem[]>([]);
  const [sharedWith, setSharedWith] = createSignal<ITagItem[]>([]);
  const [nameError, setNameError] = createSignal<string>();
  const [descriptionError, setDescriptionError] = createSignal<string>();
  const [nameTouched, setNameTouched] = createSignal(false);
  const [descriptionTouched, setDescriptionTouched] = createSignal(false);
  const [isSaving, setIsSaving] = createSignal(false);
  const [showCloseConfirm, setShowCloseConfirm] = createSignal(false);

  const hasModelsLoaded = createMemo(() => props.hasModelsLoaded?.() ?? true);

  // Fetch project users for the "Shared with" field
  const projectUsersQuery = createMemo(() => {
    const currentRegion = props.region();
    const currentProjectId = props.projectId();
    if (currentRegion === undefined || currentProjectId === undefined) {
      return undefined;
    }
    return props.tcTcpsApi.listProjectUsersQuery(
      currentRegion,
      currentProjectId
    );
  });

  // Convert project users to ITagItem format for TagEditor
  const projectUsersAsTagItems = createMemo((): ITagItem[] => {
    const query = projectUsersQuery();
    if (!query || !query.isSuccess) return [];

    return query.data
      .filter((user) => user.status === 'ACTIVE')
      .map((user) => ({
        label: `${user.firstName} ${user.lastName}`,
        value: user.id,
        isValid: true,
        id: user.id,
      }));
  });

  // Fetch existing project tags for the "Tags" field
  const projectTagsQuery = createMemo(() => {
    const currentRegion = props.region();
    const currentProjectId = props.projectId();
    if (currentRegion === undefined || currentProjectId === undefined) {
      return undefined;
    }
    return props.tcTcpsApi.listTagsQuery(currentRegion, currentProjectId);
  });

  // Convert project tags to ITagItem format for TagEditor
  const projectTagsAsTagItems = createMemo((): ITagItem[] => {
    const query = projectTagsQuery();
    if (!query || !query.isSuccess) return [];

    return query.data.map((tag) => ({
      label: tag.label,
      value: tag.id,
      isValid: true,
      id: tag.id,
      color: tag.color,
    }));
  });

  const validateName = (): boolean => {
    if (!name().trim()) {
      setNameError(t('mobileMapping.views.create.nameRequired'));
      return false;
    }
    setNameError(undefined);
    return true;
  };

  const validateDescription = (): boolean => {
    if (!description().trim()) {
      setDescriptionError(t('mobileMapping.views.create.descriptionRequired'));
      return false;
    }
    setDescriptionError(undefined);
    return true;
  };

  const validate = (): boolean => {
    setNameTouched(true);
    setDescriptionTouched(true);
    const nameValid = validateName();
    const descriptionValid = validateDescription();
    return nameValid && descriptionValid;
  };

  const handleSave = async (): Promise<void> => {
    if (!hasModelsLoaded()) {
      return;
    }

    if (!validate()) {
      return;
    }

    setIsSaving(true);

    try {
      const viewId = await props.onSave({
        name: name(),
        description: description(),
        tags: tags(),
        sharedWith: sharedWith(),
      });

      if (props.reservedViewDataConfig?.getReservedData) {
        const region = props.region();
        if (region) {
          try {
            const reservedData =
              await props.reservedViewDataConfig.getReservedData({ viewId });
            await props.tcTcpsApi.putReservedViewData(
              region,
              viewId,
              props.reservedViewDataConfig.appName,
              reservedData
            );
          } catch (reservedError: unknown) {
            logger.error(
              'Failed to save reserved view data',
              { viewId },
              asError(reservedError)
            );
            props.reservedViewDataConfig?.onReservedDataError?.(
              viewId,
              asError(reservedError)
            );
          }
        }
      }

      props.onViewCreated?.(viewId);

      NotificationEventBus.emit({
        type: 'info',
        message: t('mobileMapping.views.create.success'),
      });

      props.onClose();
    } catch (error: unknown) {
      NotificationEventBus.emit({
        type: 'error',
        message:
          error instanceof Error
            ? error.message
            : t('mobileMapping.views.create.error'),
      });
      logger.error('Error creating view', asError(error));
    } finally {
      setIsSaving(false);
    }
  };

  const isFormComplete = createMemo(
    (): boolean => name().trim().length > 0 && description().trim().length > 0
  );

  const hasChanges = (): boolean =>
    name().trim().length > 0 ||
    description().trim().length > 0 ||
    tags().length > 0 ||
    sharedWith().length > 0;

  const handleCancel = (): void => {
    if (hasChanges()) {
      setShowCloseConfirm(true);
      return;
    }
    props.onClose();
  };

  const handleCloseCancel = (): void => {
    setShowCloseConfirm(false);
  };

  const handleCloseConfirm = (): void => {
    setShowCloseConfirm(false);
    props.onClose();
  };

  const tagsLabel = createMemo(
    (): string => `${t('mobileMapping.views.create.tags')} (${tags().length})`
  );

  const sharedWithLabel = createMemo(
    (): string =>
      `${t('mobileMapping.views.create.sharedWith')} (${sharedWith().length})`
  );

  return (
    <div data-testid="create-view-tab" class="flex flex-col h-full bg-white">
      <PanelLayout
        panelTitle={t('mobileMapping.views.create.title')}
        rightActions={
          <UvIconButton
            dataTestId="create-view-tab-close"
            type="action"
            tooltip={t('general.close')}
            icon="tc-icon-content-close"
            onClick={handleCancel}
            disabled={false}
            tooltipArrow={false}
            connectStyle
          />
        }
        content={
          <div class="flex flex-col h-full">
            <Show when={!hasModelsLoaded()}>
              <div
                class="p-3 text-red-dark font-semibold text-sm"
                data-testid="models-required-error"
              >
                {t('mobileMapping.views.create.modelsRequired')}
              </div>
            </Show>

            <div class="flex-1 flex flex-col gap-4 p-3 pt-0 overflow-auto">
              {/* Name field — inline markup using input-group to match
                 Description label styling (CustomInput uses tc-custom-field
                 which renders labels differently) */}
              <div class="input-group">
                <label for="view-name">
                  {t('mobileMapping.views.create.name')}
                </label>
                <div class="input-focus-group">
                  <input
                    id="view-name"
                    name="view-name"
                    type="text"
                    class={nameTouched() && nameError() ? 'error' : ''}
                    placeholder={t(
                      'mobileMapping.views.create.namePlaceholder'
                    )}
                    value={name()}
                    onInput={(e) => {
                      setName(e.currentTarget.value);
                      validateName();
                    }}
                    onBlur={() => {
                      setNameTouched(true);
                      validateName();
                    }}
                    data-cy="create-view-name-input"
                    data-testid="create-view-name-input"
                  />
                  <div class="line" />
                </div>
                <Show when={nameTouched() && nameError()}>
                  <div class="error-message">{nameError()}</div>
                </Show>
              </div>

              {/* Description field — inline markup matching CustomTextarea
                 (cannot import CustomTextarea directly due to noShadowDOM()) */}
              <div class="input-group">
                <label for="view-description">
                  {t('mobileMapping.views.create.description')}
                </label>
                <div class="input-focus-group">
                  <textarea
                    id="view-description"
                    name="view-description"
                    class={`pl-1 pt-1 ${descriptionTouched() && descriptionError() ? 'error' : ''}`}
                    placeholder={t(
                      'mobileMapping.views.create.descriptionPlaceholder'
                    )}
                    value={description()}
                    onInput={(e) => {
                      setDescription(e.currentTarget.value);
                      validateDescription();
                    }}
                    onBlur={() => {
                      setDescriptionTouched(true);
                      validateDescription();
                    }}
                    rows={4}
                    data-cy="create-view-description-input"
                    data-testid="create-view-description-input"
                  />
                  <div class="line" />
                </div>
                <Show when={descriptionTouched() && descriptionError()}>
                  <div class="error-message">{descriptionError()}</div>
                </Show>
              </div>

              <TagEditor
                id="create-view-shared-with-input"
                title={sharedWithLabel()}
                placeHolder={t(
                  'mobileMapping.views.create.sharedWithPlaceholder'
                )}
                selectedTagList={sharedWith()}
                onTagChange={setSharedWith}
                tagList={projectUsersAsTagItems()}
                allowCustomTag={false}
                matchCase={false}
                separator={[Separators.ENTER, Separators.COMMA]}
              />

              <TagEditor
                id="create-view-tags-input"
                title={tagsLabel()}
                placeHolder={t('mobileMapping.views.create.tagsPlaceholder')}
                selectedTagList={tags()}
                onTagChange={setTags}
                tagList={projectTagsAsTagItems()}
                allowCustomTag={true}
                matchCase={false}
                separator={[Separators.ENTER, Separators.COMMA]}
              />

              {/* TODO: Add View Groups dropdown once supported by Numark */}
            </div>

            {/* Footer buttons */}
            <div class="flex justify-end gap-2 px-4 py-3 border-t border-gray-2">
              <Button
                color={BtnClasses.LINK_SECONDARY}
                content={t('mobileMapping.views.create.cancel')}
                onClick={handleCancel}
                data-cy="create-view-cancel-button"
              />
              <Button
                color={BtnClasses.PRIMARY}
                content={
                  isSaving()
                    ? t('mobileMapping.views.create.saving')
                    : t('mobileMapping.views.create.save')
                }
                icon={isSaving() ? 'tc-icon-spinner-light' : undefined}
                disabled={isSaving() || !hasModelsLoaded() || !isFormComplete()}
                onClick={() => void handleSave()}
                data-cy="create-view-save-button"
              />
            </div>
          </div>
        }
      />

      <ConfirmPopup
        show={showCloseConfirm()}
        title={t('mobileMapping.views.create.discardConfirmTitle')}
        description={t('mobileMapping.views.create.discardConfirmDescription')}
        primaryBtnLabel={t('mobileMapping.views.create.discardChanges')}
        primaryBtnClass={BtnClasses.DANGER}
        onClose={handleCloseCancel}
        onAction={handleCloseConfirm}
      />
    </div>
  );
};
